import React, { useMemo, useEffect, useState } from 'react';
import {
    Modal,
    Box,
    Typography,
    Divider,
    IconButton,
    Button,
    CircularProgress,
    Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllDieselPrices } from '../../features/dieselprice/dieselPriceApi';
import { selectDieselPriceAll, selectDieselPriceStates } from '../../features/dieselprice/dieselPriceSlice';

const formatINR = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '-';
    try {
        return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(num);
    } catch (e) {
        return String(num);
    }
};

const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return new Intl.DateTimeFormat('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(d);
};

const norm = (s) => (s || '').toLowerCase().replace(/\s+/g, '').replace(/-/g, '');

const findCity = (statesData, label) => {
    const key = norm(label);
    const synonyms = {
        panjim: ['panaji'],
        belgaum: ['belagavi'],
        bengaluru: ['bangalore'],
        delhi: ['newdelhi', 'delhi'],
        navimumbai: ['navimumbai'],
    };
    const alt = synonyms[key] || [];

    for (const [stateName, cities] of Object.entries(statesData || {})) {
        for (const [cityName, v] of Object.entries(cities || {})) {
            const cKey = norm(cityName);
            if (cKey === key || alt.includes(cKey) || cKey.includes(key) || key.includes(cKey)) {
                return { state: stateName, city: cityName, price: Number(v?.price), change: v?.change };
            }
        }
    }
    return null;
};

const DieselPricesModal = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const { loading, error, data, fetchedAt } = useSelector(selectDieselPriceAll);
    const statesData = useSelector(selectDieselPriceStates);

    const stateNames = useMemo(
        () => Object.keys(statesData || {}).sort((a, b) => a.localeCompare(b)),
        [statesData]
    );

    const [selectedState, setSelectedState] = useState('');

    useEffect(() => {
        if (!open) return;
        if (!data && !loading) {
            dispatch(fetchAllDieselPrices());
        }
    }, [open, data, loading, dispatch]);

    useEffect(() => {
        if (!stateNames.length) return;
        const preferred = statesData?.Maharashtra ? 'Maharashtra' : stateNames[0];
        if (!selectedState) {
            setSelectedState(preferred);
            return;
        }
        if (!statesData?.[selectedState]) {
            setSelectedState(preferred);
        }
    }, [stateNames, statesData, selectedState]);

    const priceDateStr = useMemo(() => formatDate(data?.scraped_at), [data?.scraped_at]);

    const globalStats = useMemo(() => {
        let min = null;
        let max = null;
        let minPlace = null;
        let maxPlace = null;
        Object.entries(statesData || {}).forEach(([stateName, cities]) => {
            Object.entries(cities || {}).forEach(([cityName, v]) => {
                const p = Number(v?.price);
                if (!Number.isFinite(p)) return;
                if (min === null || p < min) {
                    min = p;
                    minPlace = { state: stateName, city: cityName };
                }
                if (max === null || p > max) {
                    max = p;
                    maxPlace = { state: stateName, city: cityName };
                }
            });
        });
        return { min, max, minPlace, maxPlace };
    }, [statesData]);

    const stateSummaries = useMemo(() => {
        return stateNames.map((stateName) => {
            const cities = statesData[stateName] || {};
            let min = null;
            let max = null;
            let hasChange = false;
            Object.values(cities).forEach((v) => {
                const p = Number(v?.price);
                if (!Number.isFinite(p)) return;
                if (min === null || p < min) min = p;
                if (max === null || p > max) max = p;
                const delta = Number(v?.change);
                if (Number.isFinite(delta) && delta !== 0) hasChange = true;
            });
            return {
                state: stateName,
                min,
                max,
                count: Object.keys(cities).length,
                hasChange,
            };
        });
    }, [stateNames, statesData]);

    const selectedCities = useMemo(() => {
        if (!selectedState) return [];
        const cities = statesData[selectedState] || {};
        return Object.entries(cities)
            .map(([city, v]) => ({
                city,
                price: Number(v?.price),
                change: v?.change,
            }))
            .sort((a, b) => a.city.localeCompare(b.city));
    }, [statesData, selectedState]);

    const quickGroups = [
        { title: 'Mumbai', cities: ['Mumbai', 'Navi Mumbai', 'Thane'] },
        { title: 'Near Maharashtra', cities: ['Panjim', 'Surat', 'Belgaum', 'Indore'] },
        { title: 'Metros', cities: ['Delhi', 'Hyderabad', 'Chennai', 'Bengaluru', 'Kolkata'] },
    ];

    const mumbai = findCity(statesData, 'Mumbai');

    const colors = {
        ink: '#1b1f3b',
        muted: '#667085',
        panel: '#ffffff',
        panelAlt: '#f4f5ff',
        accent: '#5b5ce2',
        accentDark: '#383d94',
        accentSoft: '#e6e8ff',
        crimson: '#cc1f35',
        emerald: '#0f8b6b',
        border: '#e1e4ff',
        shadow: 'rgba(27, 31, 59, 0.12)',
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'fixed',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(13, 16, 35, 0.55)',
                    p: { xs: 1, sm: 2 },
                }}
            >
                <Box
                    sx={{
                        width: { xs: '96vw', md: '80vw' },
                        height: { xs: '92vh', md: '80vh' },
                        maxWidth: 1400,
                        maxHeight: '86vh',
                        bgcolor: colors.panelAlt,
                        borderRadius: 4,
                        boxShadow: `0 24px 70px ${colors.shadow}`,
                        border: `1px solid ${colors.border}`,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            px: { xs: 2, md: 3 },
                            py: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: `linear-gradient(135deg, ${colors.accentDark}, ${colors.accent})`,
                            color: '#fff',
                        }}
                    >
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
                                Diesel Price Overview
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.85 }}>
                                Price date: {priceDateStr}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>
                    <Divider />
                    <Box
                        sx={{
                            flex: 1,
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr', lg: '1.05fr 1fr 1.2fr' },
                            gap: 2,
                            p: { xs: 2, md: 3 },
                            overflow: 'hidden',
                        }}
                    >
                        {/* Left Column */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', pr: 0.5 }}>
                            <Box
                                sx={{
                                    bgcolor: colors.panel,
                                    borderRadius: 3,
                                    p: 2,
                                    border: `1px solid ${colors.border}`,
                                    boxShadow: `0 12px 28px ${colors.shadow}`,
                                }}
                            >
                                <Typography variant="caption" sx={{ color: colors.muted, fontWeight: 600 }}>
                                    Price date
                                </Typography>
                                <Typography variant="h6" sx={{ color: colors.ink, fontWeight: 700 }}>
                                    {priceDateStr}
                                </Typography>
                                <Box
                                    sx={{
                                        mt: 1.5,
                                        bgcolor: 'rgba(204, 31, 53, 0.12)',
                                        border: '1px solid rgba(204, 31, 53, 0.25)',
                                        borderRadius: 2,
                                        px: 1.5,
                                        py: 1,
                                    }}
                                >
                                    <Typography variant="body2" sx={{ color: colors.crimson, fontWeight: 700 }}>
                                        Prices may vary slightly by pump/operator. Experimental feature—please verify before making financial decisions.
                                    </Typography>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    bgcolor: 'rgba(91, 92, 226, 0.12)',
                                    borderRadius: 3,
                                    p: 2,
                                    border: `1px solid ${colors.border}`,
                                }}
                            >
                                <Typography variant="h6" sx={{ color: colors.accentDark, fontWeight: 700 }}>
                                    Mumbai
                                </Typography>
                                <Typography variant="h4" sx={{ color: colors.ink, fontWeight: 800 }}>
                                    {mumbai && Number.isFinite(mumbai.price) ? `₹${formatINR(mumbai.price)}` : '-'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: colors.muted }}>
                                    Mumbai, Maharashtra
                                </Typography>
                                {mumbai?.change !== undefined && mumbai?.change !== null ? (
                                    <Chip
                                        label={`Price change: ${Number(mumbai.change) > 0 ? '+' : ''}${mumbai.change}`}
                                        size="small"
                                        sx={{
                                            mt: 1.5,
                                            bgcolor: Number(mumbai.change) > 0 ? 'rgba(204, 31, 53, 0.12)' : 'rgba(15, 139, 107, 0.12)',
                                            color: Number(mumbai.change) > 0 ? colors.crimson : colors.emerald,
                                            fontWeight: 700,
                                        }}
                                    />
                                ) : null}
                            </Box>

                            <Box
                                sx={{
                                    bgcolor: colors.panel,
                                    borderRadius: 3,
                                    p: 2,
                                    border: `1px solid ${colors.border}`,
                                }}
                            >
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.ink }}>
                                    Nationwide Range Today
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" sx={{ color: colors.muted }}>
                                            Least price today
                                        </Typography>
                                        <Typography variant="h6" sx={{ color: colors.emerald, fontWeight: 700 }}>
                                            ₹{formatINR(globalStats.min)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: colors.muted }}>
                                            {globalStats.minPlace ? `${globalStats.minPlace.city}, ${globalStats.minPlace.state}` : ''}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" sx={{ color: colors.muted }}>
                                            Highest price today
                                        </Typography>
                                        <Typography variant="h6" sx={{ color: colors.crimson, fontWeight: 700 }}>
                                            ₹{formatINR(globalStats.max)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: colors.muted }}>
                                            {globalStats.maxPlace ? `${globalStats.maxPlace.city}, ${globalStats.maxPlace.state}` : ''}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    bgcolor: colors.panel,
                                    borderRadius: 3,
                                    p: 2,
                                    border: `1px solid ${colors.border}`,
                                }}
                            >
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.ink, mb: 1 }}>
                                    Quick Prices
                                </Typography>
                                {quickGroups.map((group, gi) => (
                                    <Box key={group.title} sx={{ mb: gi < quickGroups.length - 1 ? 2 : 0 }}>
                                        <Typography variant="caption" sx={{ color: colors.muted, fontWeight: 700 }}>
                                            {group.title}
                                        </Typography>
                                        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {group.cities.map((label) => {
                                                const hit = findCity(statesData, label);
                                                return (
                                                    <Box
                                                        key={`${group.title}-${label}`}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            bgcolor: 'rgba(91, 92, 226, 0.06)',
                                                            borderRadius: 2,
                                                            px: 1.5,
                                                            py: 1,
                                                        }}
                                                    >
                                                        <Box>
                                                            <Typography variant="body2" sx={{ color: colors.ink, fontWeight: 600 }}>
                                                                {label}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: colors.muted }}>
                                                                {hit?.state || '-'}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: colors.ink }}>
                                                                {hit && Number.isFinite(hit.price) ? `₹${formatINR(hit.price)}` : '-'}
                                                            </Typography>
                                                            {hit?.change ? (
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: Number(hit.change) > 0 ? colors.crimson : colors.emerald,
                                                                        fontWeight: 700,
                                                                    }}
                                                                >
                                                                    {Number(hit.change) > 0 ? '+' : ''}{hit.change}
                                                                </Typography>
                                                            ) : null}
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>

                            {loading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: colors.muted }}>
                                    <CircularProgress size={18} />
                                    <Typography variant="caption">Loading prices...</Typography>
                                </Box>
                            ) : null}

                            {error ? (
                                <Box sx={{ bgcolor: '#fff0f2', borderRadius: 2, p: 1.5, border: '1px solid #ffd6db' }}>
                                    <Typography variant="caption" sx={{ color: colors.crimson }}>
                                        {String(error)}
                                    </Typography>
                                </Box>
                            ) : null}
                        </Box>

                        {/* Middle Column */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', pr: 0.5 }}>
                            <Box
                                sx={{
                                    bgcolor: colors.panel,
                                    borderRadius: 3,
                                    p: 2,
                                    border: `1px solid ${colors.border}`,
                                }}
                            >
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.ink, mb: 1 }}>
                                    States Overview
                                </Typography>
                                <Typography variant="caption" sx={{ color: colors.muted }}>
                                    Tap a state to view city prices.
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {stateSummaries.map((row) => {
                                    const isActive = row.state === selectedState;
                                    return (
                                        <Box
                                            key={row.state}
                                            onClick={() => setSelectedState(row.state)}
                                            sx={{
                                                cursor: 'pointer',
                                                bgcolor: isActive ? colors.accentSoft : colors.panel,
                                                borderRadius: 2.5,
                                                px: 2,
                                                py: 1.5,
                                                border: `1px solid ${isActive ? colors.accent : colors.border}`,
                                                boxShadow: isActive ? `0 10px 24px ${colors.shadow}` : 'none',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    borderColor: colors.accent,
                                                    transform: 'translateY(-1px)',
                                                },
                                            }}
                                        >
                                            <Typography variant="body1" sx={{ color: colors.ink, fontWeight: 700 }}>
                                                {row.state}
                                            </Typography>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                                                    <Typography variant="caption" sx={{ color: colors.muted }}>
                                                        ₹{formatINR(row.min)} - ₹{formatINR(row.max)}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {row.hasChange ? (
                                                            <Chip
                                                                label="Price Changed"
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: 'rgba(204, 31, 53, 0.12)',
                                                                    color: colors.crimson,
                                                                    fontWeight: 700,
                                                                }}
                                                            />
                                                        ) : null}
                                                        <Typography variant="caption" sx={{ color: colors.muted }}>
                                                            {row.count} cities
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                            </Box>
                        </Box>

                        {/* Right Column */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', pr: 0.5 }}>
                            <Box
                                sx={{
                                    bgcolor: colors.panel,
                                    borderRadius: 3,
                                    p: 2,
                                    border: `1px solid ${colors.border}`,
                                }}
                            >
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.ink }}>
                                    {selectedState || 'Select a state'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: colors.muted }}>
                                    Detailed city prices
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {selectedCities.map((row) => (
                                    <Box
                                        key={`${selectedState}-${row.city}`}
                                        sx={{
                                            bgcolor: colors.panel,
                                            borderRadius: 2.5,
                                            px: 2,
                                            py: 1.5,
                                            border: `1px solid ${colors.border}`,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2" sx={{ color: colors.ink, fontWeight: 600 }}>
                                                {row.city}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: colors.ink, fontWeight: 700 }}>
                                                ₹{formatINR(row.price)}
                                            </Typography>
                                        </Box>
                                        {row.change !== undefined && row.change !== null ? (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: Number(row.change) > 0 ? colors.crimson : colors.emerald,
                                                    fontWeight: 700,
                                                }}
                                            >
                                                Price change: {Number(row.change) > 0 ? '+' : ''}{row.change}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                ))}
                                {!selectedCities.length && !loading ? (
                                    <Typography variant="caption" sx={{ color: colors.muted }}>
                                        No city data available.
                                    </Typography>
                                ) : null}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};

export default DieselPricesModal;
